// Shell.jsx (padding tweak only)
import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'

import { useChatRefs } from '@/contexts/ChatRefs/useChatRefs'
import { toast } from 'sonner'
import {
  useKeyboardNavigation,
  useKeyboardShortcut,
} from '@/hooks/useKeyboardShortcut'
import { cn } from '@/lib/utils'
import { useCurrentConversationStore } from '@/store/useCurrentConversationStore'
import { useUIStore } from '@/store/useUIStore'

import useChatScroll from '../../hooks/chat/useChatScroll'
import ChatInput from '../chat/ChatInput'
import ChatPane from '../chat/ChatPane'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../ui/resizable'
import ProjectGrid from '../projects/ProjectGrid'
import ProjectPanel from '@/components/projects/ProjectPanel'
import DesktopHeader from './DesktopHeader'
import MobileHeader from './MobileHeader'
import { Card } from '../ui/Card'

export default function Shell() {
  const messages = useCurrentConversationStore(s => s.messages)
  const toastMessage = useCurrentConversationStore(s => s.toastMessage)
  const setToastMessage = useCurrentConversationStore(s => s.setToastMessage)
  const { showScrollButton, scrollToBottom } = useChatScroll(messages)
  const { messagesContainerRef } = useChatRefs()

  const [smoothScroll, setSmoothScroll] = useState(true)
  const streamPhase = useCurrentConversationStore(s => s.streamPhase)
  const isStreaming = streamPhase === 'streaming' || streamPhase === 'loading'

  const toggleSidebar = useUIStore(state => state.toggleSidebar)
  useKeyboardShortcut([
    { key: 's', metaKey: true, shiftKey: true, callback: () => toggleSidebar() },
  ])
  useKeyboardNavigation([{ key: 'o', metaKey: true, shiftKey: true, path: '/chat' }])

  useEffect(() => {
    if (messages.length === 0 || isStreaming) return
    const el = messagesContainerRef.current
    if (el) el.scrollTop = el.scrollHeight
    const t = setTimeout(() => setSmoothScroll(true), 200)
    return () => clearTimeout(t)
  }, [messages.length, isStreaming, messagesContainerRef])

  useEffect(() => {
    if (toastMessage) {
      toast('Notification', { description: toastMessage.message })
      setToastMessage(null)
    }
  }, [toastMessage, setToastMessage])

  const isNewChat = useCurrentConversationStore(state => state.id) === null
  const conversationIsLoading = useCurrentConversationStore(state => state.loading)
  const loadingTimerRef = useRef(null)
  const [showSpinner, setShowSpinner] = useState(false)

  useEffect(() => {
    if (conversationIsLoading && messages.length === 0) {
      loadingTimerRef.current = setTimeout(() => setShowSpinner(true), 1500)
    } else {
      setShowSpinner(false)
      clearTimeout(loadingTimerRef.current)
    }
    return () => loadingTimerRef.current && clearTimeout(loadingTimerRef.current)
  }, [conversationIsLoading, messages.length])

  return (
    <div id="right-column" className="relative flex h-full max-w-full flex-1 flex-col">
      <MobileHeader isNewChat={isNewChat} />

      <main
        id="main"
        className={cn(
          'relative size-full flex-1 transition-[width,background-color] duration-300',
          isNewChat
            ? 'overflow-hidden py-12'
            : 'overflow-auto pt-12',
        )}
      >
        <motion.div id="thread" className="group/thread size-full @container/thread">
          <div role="presentation" className="composer-parent flex h-full flex-col focus-visible:outline-0">
            <DesktopHeader isNewChat={isNewChat} />

            <div
              className={cn(
                'flex basis-auto flex-col',
                isNewChat ? 'flex-1 min-h-0' : '-mb-4 flex grow overflow-hidden',
              )}
            >
              {isNewChat ? (
                <div className="flex h-full w-full flex-col gap-4 px-4 pt-4 pb-16 sm:px-6">
                  <div className="mx-auto w-full max-w-6xl">
                    <Card
                      className={cn(
                        'relative overflow-hidden rounded-[24px] border border-white/10 bg-white/8',
                        'backdrop-blur-md shadow-[0_18px_64px_-26px_rgba(0,0,0,0.55)]',
                        'new-chat:border-white/12 new-chat:bg-white/10',
                      )}
                    >
                      <div aria-hidden className="absolute inset-0 bg-gradient-to-tr from-white/6 via-white/3 to-transparent" />
                      <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div>
                            <h1 className="text-lg font-semibold tracking-tight text-white/94">
                              Project Pilot
                            </h1>
                            <p className="text-sm text-white/70">
                              A focused workspace for chat + projects.
                            </p>
                          </div>
                        </div>
                        <div className="text-xs text-white/65">
                          Tip: open or create a project thread to start collaborating.
                        </div>
                      </div>
                    </Card>
                  </div>

                  <div className="mx-auto flex w-full max-w-6xl flex-1 min-h-0">
                    <ProjectGrid
                      className={cn(
                        'w-full rounded-xl flex h-full min-h-0 flex-col',
                        'bg-white/10 text-white',
                        'border border-white/10',
                        'backdrop-blur-md',
                        'shadow-[0_20px_70px_-28px_rgba(0,0,0,0.55)]',
                      )}
                    />
                  </div>
                </div>
              ) : (
                <div className="relative h-full flex-1">
                  <div className="hidden h-full lg:flex">
                    <ResizablePanelGroup direction="horizontal">
                      <ResizablePanel defaultSize={55} minSize={35}>
                        <ChatPane
                          messages={messages}
                          messagesContainerRef={messagesContainerRef}
                          showScrollButton={showScrollButton}
                          scrollToBottom={scrollToBottom}
                          smoothScroll={smoothScroll}
                          showSpinner={showSpinner}
                        />
                      </ResizablePanel>
                      <ResizableHandle withHandle className="bg-transparent" />
                      <ResizablePanel defaultSize={45} minSize={25}>
                        <ProjectPanel />
                      </ResizablePanel>
                    </ResizablePanelGroup>
                  </div>

                  <div className="flex h-full lg:hidden">
                    <ChatPane
                      messages={messages}
                      messagesContainerRef={messagesContainerRef}
                      showScrollButton={showScrollButton}
                      scrollToBottom={scrollToBottom}
                      smoothScroll={smoothScroll}
                      showSpinner={showSpinner}
                    />
                  </div>
                </div>
              )}
            </div>

            {isNewChat ? (
              <div className="absolute inset-x-0 bottom-0 z-[70] pb-4">
                <div className="pointer-events-auto mx-auto w-full max-w-6xl px-4 sm:px-6">
                  <div className="relative mx-auto max-w-[56rem]">
                    <div className="pointer-events-none absolute inset-x-0 -top-24 h-24 bg-gradient-to-t from-black/40 via-black/15 to-transparent blur-md" />
                    <div
                      className={cn(
                        'relative overflow-hidden rounded-[20px]',
                        'border border-white/12 bg-white/10 backdrop-blur-lg',
                        'shadow-[0_16px_55px_-24px_rgba(0,0,0,0.6)]',
                      )}
                    >
                      <div aria-hidden className="absolute inset-0 bg-gradient-to-tr from-white/6 via-transparent to-transparent" />
                      <div className="p-3">
                        <ChatInput variant="embedded" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <ChatInput />
            )}
          </div>
        </motion.div>
      </main>
    </div>
  )
}
